import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Modal } from "react-bootstrap";
import useStrategyCanvasReviewStore, {
  useStrategyCanvasReviewStoreSelector,
} from "../_redux/StrategyCanvasReviewStore";
import "./StrategyCanvasCanvasMode.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Ported, read-only, from 10x-frontend's
// StrategyCanvasChat/StrategyCanvasPreviewPanel/StrategyCanvasBody
// (StrategyCanvasBody.js + _strategy-canvas-body.scss) and its skeleton
// (StrategyCanvasBodySkeleton.js + _strategy-canvas-body-skeleton.scss).
// Same markup/classes/placement as the reference design, minus: edit
// buttons, popup/expand buttons + modal, hover-reveal button groups, and the
// "active block" highlight state - this view is read-only and just shows
// each block's value straight from the canvas-mode API.
// ─────────────────────────────────────────────────────────────────────────────

const PLACEHOLDER = "Not added yet";
const DRIVER_COUNT = 3;

// The reference design's slugs (bhag, general_needs, key_partners, ...)
// differ from the real canvas-mode API's block_key names - map API keys onto
// the reference slugs so the ported layout below can stay identical to the
// source design.
const SLUG_MAP = {
  bhag: "bhag",
  true_north_metric: "true_north_metric",
  winning_moves: "winning_moves",
  growth_drivers: "growth_drivers",
  target_segment: "target_segment",
  niche_positioning: "niche_positioning",
  unique_value_proposition: "unique_value_prop",
  offering: "offering",
  competitive_advantage_sources: "competitive_advantage",
  competitors: "competitors",
  partners: "key_partners",
  core_channels: "core_channels",
  customer_relationships: "customer_relationships",
  activities: "key_activities",
  resources: "key_resources",
  revenue_model_pricing: "revenue_model",
  ue_cltv_cac: "unit_economics",
  cost_structure: "cost_structure",
  cash_cycle: "cash_cycle",
  pct_full_potential_achieved: "full_potential",
  primary_strategy_to_grow_value: "primary_strategy",
  adjacency_strategy: "adjacency_strategy",
  mission: "mission",
  vision: "vision",
  purpose: "purpose",
  values: "values",
};

// "generic_niche_needs" is a single API block but the reference design shows
// General Needs / Niche Needs as two independent halves of a split cell.
const splitGenericNiche = (raw) => {
  if (!raw) return { general: null, niche: null };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return {
        general: parsed.general_needs ?? parsed.general ?? null,
        niche: parsed.niche_needs ?? parsed.niche ?? null,
      };
    }
  } catch (_) {}
  if (typeof raw === "string" && raw.includes("|")) {
    const [general, niche] = raw.split("|");
    return { general: general?.trim() || null, niche: niche?.trim() || null };
  }
  return { general: raw, niche: null };
};

const buildFormData = (canvasMode) => {
  const formData = {};
  (canvasMode?.layers || []).forEach((layer) => {
    (layer.blocks || []).forEach((block) => {
      if (block.block_key === "generic_niche_needs") {
        const { general, niche } = splitGenericNiche(block.value);
        formData.general_needs = general;
        formData.niche_needs = niche;
        return;
      }
      const slug = SLUG_MAP[block.block_key] || block.block_key;
      formData[slug] = block.value ?? null;
    });
  });
  return formData;
};

// ─── Block value ──────────────────────────────────────────────────────────────
// UAT (JK): "copy not fitting in a small block" and "formatting could be
// neater". Two changes, both here so every cell gets them:
//
// 1. Markdown, not plain text. Block values are AI-written and routinely carry
//    bullets and **bold** terms; the CEO's canvas already renders them, the
//    coach's showed the raw asterisks. Single newlines are turned into hard
//    breaks first, because markdown would otherwise fold them into one line —
//    `white-space: pre-line` used to preserve them.
// 2. "View full" instead of a hidden scroll. Cells cap their height (the grid
//    has to line up), and the value used to scroll with its scrollbar hidden,
//    so overflow was invisible. The value now clips with a fade, and a
//    "View full" link appears only when it actually overflows — measured, not
//    guessed from length, since cell widths vary across the grid.
const toMarkdown = (text) => String(text).replace(/([^\n])\n(?!\n)/g, "$1  \n");

const BlockValue = ({ value, title }) => {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !value) {
      setOverflowing(false);
      return undefined;
    }
    const measure = () =>
      setOverflowing(el.scrollHeight - el.clientHeight > 1);
    measure();
    // Cells resize with the panel (full-screen toggle, window width), so the
    // answer can change without the value changing.
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [value]);

  if (!value) return <p className="scb-cell__value">{PLACEHOLDER}</p>;

  return (
    <>
      <div
        ref={ref}
        className={[
          "scb-cell__value",
          "scb-cell__value--md",
          overflowing && "scb-cell__value--clipped",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <ReactMarkdown>{toMarkdown(value)}</ReactMarkdown>
      </div>
      {overflowing && (
        <button
          type="button"
          className="scb-cell__view-full"
          onClick={() => setOpen(true)}
        >
          View full
        </button>
      )}
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        centered
        dialogClassName="scb-value-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="scb-value-modal__title">
            {title || "Block"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="scb-value-modal__body">
          <ReactMarkdown>{toMarkdown(value)}</ReactMarkdown>
        </Modal.Body>
      </Modal>
    </>
  );
};

// ─── Primitives ───────────────────────────────────────────────────────────────

const Cell = ({ value, label, title, fill = false }) => {
  const hasFilled = !!value;
  return (
    <div
      className={[
        "scb-cell",
        fill && "scb-cell--fill",
        hasFilled && "scb-cell--filled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && (
        <div className="scb-cell__title-row">
          <p className="scb-cell__title">{label}</p>
        </div>
      )}
      <BlockValue value={value} title={label || title} />
    </div>
  );
};

const Field = ({
  label,
  innerLabel,
  value,
  grow = false,
  small = false,
  children,
}) => {
  const hasFilled = !!value;
  return (
    <div
      className={[
        "scb-field",
        grow && "scb-field--grow",
        small && "scb-field--small",
        hasFilled && "scb-field--filled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && (
        <div className="scb-field__label-row">
          <p className="scb-field__label">{label}</p>
        </div>
      )}
      {children ?? (
        <Cell value={value} label={innerLabel} title={label} fill={grow} />
      )}
    </div>
  );
};

const Row = React.forwardRef(({ children, mod, gap = 8 }, ref) => (
  <div
    ref={ref}
    className={["scb-row-2col", mod && `scb-row-2col--${mod}`]
      .filter(Boolean)
      .join(" ")}
    style={{ gap }}
  >
    {children}
  </div>
));

const Band = ({ color = "teal", label, children }) => (
  <div className={`scb-band-row scb-band-row--${color}`}>
    <div className="scb-band-row__bar">
      <span className="scb-band-row__label">{label}</span>
    </div>
    <div className="scb-band-row__content">{children}</div>
  </div>
);

const SectionHeader = ({ children, className = "" }) => (
  <p className={`scb-section-header${className ? ` ${className}` : ""}`}>
    {children}
  </p>
);

const Subsection = ({ title, children }) => (
  <div className="scb-body__subsection">
    {title && <SectionHeader>{title}</SectionHeader>}
    {children}
  </div>
);

const RepeatableRow = ({ items, formData, small = false, className = "" }) => (
  <div
    className={`scb-repeatable__row scb-repeatable__row--${items.length}${className ? ` ${className}` : ""}`}
  >
    {items.map(({ label, slug }) => (
      <Field key={slug} label={label} value={formData[slug]} small={small} />
    ))}
  </div>
);

// ─── Growth Drivers table ─────────────────────────────────────────────────────

const parseGrowthDrivers = (raw) => {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    if (data && Array.isArray(data.drivers)) return data;
  } catch (_) {}
  return null;
};

const CURRENCY_SYMBOLS = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
  SGD: "S$",
  AED: "د.إ",
  CNY: "¥",
};

const compactNum = (num, divisor, suffix, symbol) => {
  const result = parseFloat((num / divisor).toFixed(1));
  return `${symbol}${result}${suffix}`;
};

const formatGrowthValue = (val, unit) => {
  if (val === undefined || val === null) return "/";
  const normalizedUnit = (unit || "").toString().toLowerCase();
  if (normalizedUnit === "percent") return `${val}%`;
  if (normalizedUnit === "count" || !unit) return String(val);

  const num = Number(val);
  if (isNaN(num)) return String(val);

  if (normalizedUnit === "inr") {
    const s = "₹";
    if (num >= 10000000) return compactNum(num, 10000000, "Cr", s);
    if (num >= 100000) return compactNum(num, 100000, "L", s);
    if (num >= 1000) return compactNum(num, 1000, "K", s);
    return `${s}${num}`;
  }

  const symbol = CURRENCY_SYMBOLS[normalizedUnit.toUpperCase()] ?? `${unit} `;
  if (symbol) {
    if (num >= 1_000_000_000_000)
      return compactNum(num, 1_000_000_000_000, "T", symbol);
    if (num >= 1_000_000_000)
      return compactNum(num, 1_000_000_000, "B", symbol);
    if (num >= 1_000_000) return compactNum(num, 1_000_000, "M", symbol);
    if (num >= 1_000) return compactNum(num, 1_000, "K", symbol);
    return `${symbol}${num}`;
  }

  if (num >= 1_000_000_000) return compactNum(num, 1_000_000_000, "B", "");
  if (num >= 1_000_000) return compactNum(num, 1_000_000, "M", "");
  if (num >= 1_000) return compactNum(num, 1_000, "K", "");
  return String(num);
};

const GrowthDriverRow = ({
  index,
  name,
  startVal,
  endVal,
  unit,
  hasFilled,
}) => (
  <div
    className={[
      "scb-growth-drivers__row",
      hasFilled && "scb-growth-drivers__row--filled",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <div className="scb-growth-drivers__row-name scb-growth-drivers__row-name--num">
      <ol start={index + 1} className="scb-growth-drivers__list">
        <li>{name ?? PLACEHOLDER}</li>
      </ol>
    </div>
    <div className="scb-growth-drivers__row-cell">
      {formatGrowthValue(startVal, unit)}
    </div>
    <div className="scb-growth-drivers__row-cell">
      {formatGrowthValue(endVal, unit)}
    </div>
  </div>
);

const GrowthDriversTable = ({ value }) => {
  const parsed = React.useMemo(() => parseGrowthDrivers(value), [value]);
  const hasFilled = !!parsed;
  const drivers = parsed?.drivers ?? Array(DRIVER_COUNT).fill(null);
  const timeframe = parsed?.timeframe ?? null;
  const revenue = parsed?.revenue ?? null;
  const profit = parsed?.profit ?? null;

  return (
    <div className="scb-growth-drivers">
      <div className="scb-growth-drivers__head">
        <span className="scb-growth-drivers__title">Growth Drivers</span>
        <span className="scb-growth-drivers__dates">
          <span>Start {timeframe?.start ?? "QQ/YY"}</span>
          <span>End {timeframe?.end ?? "QQ/YY"}</span>
        </span>
      </div>

      {Array.from({ length: DRIVER_COUNT }, (_, i) => {
        const d = drivers[i] ?? null;
        return (
          <GrowthDriverRow
            key={i}
            index={i}
            name={d?.name ?? null}
            startVal={d?.start ?? null}
            endVal={d?.end ?? null}
            unit={d?.unit}
            hasFilled={hasFilled}
          />
        );
      })}

      {[
        { label: "Revenue", data: revenue },
        { label: "Profit", data: profit },
      ].map(({ label, data }) => (
        <div key={label} className="scb-growth-drivers__row">
          <div className="scb-growth-drivers__row-name">
            <span className="scb-growth-drivers__row-bold">{label}</span>
          </div>
          <div className="scb-growth-drivers__row-cell scb-growth-drivers__row-cell--wide">
            {formatGrowthValue(data?.start, data?.unit)}
          </div>
          <div className="scb-growth-drivers__row-cell scb-growth-drivers__row-cell--wide">
            {formatGrowthValue(data?.end, data?.unit)}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Customer Focus inner (keeps ResizeObserver - pure layout, not interactive) ─

const CustomerFocusInner = ({ formData }) => {
  const leftColRef = useRef(null);
  const rowRef = useRef(null);

  useEffect(() => {
    const leftCol = leftColRef.current;
    const row = rowRef.current;
    if (!leftCol || !row) return;

    const observer = new ResizeObserver(() => {
      row.style.setProperty("--left-col-h", `${leftCol.offsetHeight}px`);
    });
    observer.observe(leftCol);
    row.style.setProperty("--left-col-h", `${leftCol.offsetHeight}px`);
    return () => observer.disconnect();
  }, []);

  const splitFilled = !!formData.general_needs || !!formData.niche_needs;

  return (
    <Row mod="customer-inner" ref={rowRef}>
      <div ref={leftColRef} className="scb-body__customer-left">
        <Cell value={formData.target_segment} label="Target Segment" />
        <div
          className={[
            "scb-cell",
            "scb-cell--split",
            splitFilled && "scb-cell--filled",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="scb-cell__split-col">
            <div className="scb-cell__sub-label-row">
              <p className="scb-cell__sub-label">General Needs</p>
            </div>
            <BlockValue value={formData.general_needs} title="General Needs" />
          </div>
          <div className="scb-cell__divider" />
          <div className="scb-cell__split-col">
            <div className="scb-cell__sub-label-row">
              <p className="scb-cell__sub-label">Niche Needs</p>
            </div>
            <BlockValue value={formData.niche_needs} title="Niche Needs" />
          </div>
        </div>
      </div>
      <Field
        innerLabel="Niche Positioning"
        value={formData.niche_positioning}
        grow
      />
    </Row>
  );
};

// ─── Band sections ────────────────────────────────────────────────────────────

const GrowthStrategyBand = ({ formData }) => (
  <Band color="teal" label="growth strategy">
    <div className="scb-body__section">
      <Row mod="bhag">
        <Field
          label="Our BHAG (Big Hairy Audacious Goal)"
          value={formData.bhag}
          grow
        />
        <Field
          label="True North Metric"
          value={formData.true_north_metric}
          grow
        />
      </Row>
      <Row mod="winning">
        <Field label="Winning Moves" value={formData.winning_moves} grow />
        <div className="scb-body__drivers-col">
          <GrowthDriversTable value={formData.growth_drivers} />
        </div>
      </Row>
    </div>
  </Band>
);

const GrowthPlayBand = ({ formData }) => (
  <Band color="dark" label="Competitive strategy">
    <div className="scb-body__section">
      <Subsection
        title={
          <>
            CUSTOMERS &amp; PROMISE{" "}
            <span className="scb-section-header--normal">
              (Where we play and how we win)
            </span>
          </>
        }
      >
        <Row mod="customers-outer">
          <div className="scb-customers-col">
            <p className="scb-field__label">Customer Focus</p>
            <CustomerFocusInner formData={formData} />
          </div>

          <div className="scb-customers-col">
            <p className="scb-field__label">Remarkable Promise</p>
            <div className="scb-body__promise-grid">
              <Cell
                value={formData.unique_value_prop}
                label="Unique Value Proposition"
              />
              <Cell value={formData.offering} label="Offering" />
            </div>
          </div>
        </Row>
      </Subsection>

      <Subsection title="Competitive Advantage">
        <Row mod="competitive">
          <Field
            label="Sources & Description of Defendable Competitive Advantage"
            value={formData.competitive_advantage}
            grow
          />
          <Field label="Competitors" value={formData.competitors} grow />
        </Row>
      </Subsection>

      {/* Repeatable & Economic Models - single CSS grid so row 2 (r1/e1) and
          row 3 (r2/e2) each span both columns and sync heights across the
          two columns automatically. */}
      <div className="scb-models-grid">
        <SectionHeader className="scb-models-grid__hdr scb-models-grid__hdr--r">
          Repeatable Model
        </SectionHeader>
        <SectionHeader className="scb-models-grid__hdr scb-models-grid__hdr--e">
          Economic Model
        </SectionHeader>

        <RepeatableRow
          className="scb-models-grid__r1"
          formData={formData}
          items={[
            { label: "Key Partners", slug: "key_partners" },
            { label: "Core Channels", slug: "core_channels" },
            { label: "Customer Relationships", slug: "customer_relationships" },
          ]}
        />
        <RepeatableRow
          className="scb-models-grid__e1"
          small
          formData={formData}
          items={[
            { label: "Revenue Model, Pricing", slug: "revenue_model" },
            { label: "UE = CLTV / CAC", slug: "unit_economics" },
          ]}
        />

        <RepeatableRow
          className="scb-models-grid__r2"
          formData={formData}
          items={[
            { label: "Key Activities", slug: "key_activities" },
            { label: "Key Resources", slug: "key_resources" },
          ]}
        />
        <RepeatableRow
          className="scb-models-grid__e2"
          small
          formData={formData}
          items={[
            { label: "Cost Structure", slug: "cost_structure" },
            { label: "Cash Cycle", slug: "cash_cycle" },
          ]}
        />
      </div>
    </div>
  </Band>
);

const CoreStrategyBand = ({ formData }) => (
  <Band color="core" label="core strategy">
    <div className="scb-body__section">
      <Row mod="core">
        <Field label="Core Business Strategy" grow>
          <Row mod="core-inner">
            <Field
              innerLabel="% of Full Potential Achieved"
              value={formData.full_potential}
              grow
            />
            <Field
              innerLabel="Primary Strategy to Grow Value"
              value={formData.primary_strategy}
              grow
            />
          </Row>
        </Field>
        <Field
          label="Adjacency Strategy"
          value={formData.adjacency_strategy}
          grow
        />
      </Row>
    </div>
  </Band>
);

const FOOTER_ITEMS = [
  { label: "Mission", slug: "mission" },
  { label: "Vision", slug: "vision" },
  { label: "Purpose", slug: "purpose" },
  { label: "Values", slug: "values" },
];

const CanvasFooter = ({ formData }) => (
  <Band color="dna" label="business DNA">
    <div className="scb-footer-grid">
      {FOOTER_ITEMS.map(({ label, slug }) => (
        <Field key={slug} label={label} value={formData[slug]} />
      ))}
    </div>
  </Band>
);

export const CanvasModeBody = ({ canvasMode }) => {
  const formData = buildFormData(canvasMode);
  return (
    <div className="scb-body">
      <div className="scb-body__bands">
        <GrowthStrategyBand formData={formData} />
        <GrowthPlayBand formData={formData} />
        <CoreStrategyBand formData={formData} />
      </div>
      <div className="scb-body__divider" />
      <CanvasFooter formData={formData} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton — ported verbatim (shape) from StrategyCanvasBodySkeleton.js
// ─────────────────────────────────────────────────────────────────────────────

const Sk = ({ w = "100%", h = 14, className = "" }) => (
  <div
    className={`scbs-shimmer ${className}`}
    style={{ width: w, height: h, borderRadius: 4 }}
  />
);

const SkCell = ({ fill = false }) => (
  <div
    className={["scbs-cell", fill && "scbs-cell--fill"]
      .filter(Boolean)
      .join(" ")}
  >
    <Sk w="55%" h={11} />
    <Sk w="90%" h={13} className="scbs-cell__val" />
  </div>
);

const SkField = ({ grow = false }) => (
  <div
    className={["scbs-field", grow && "scbs-field--grow"]
      .filter(Boolean)
      .join(" ")}
  >
    <Sk w="45%" h={11} className="scbs-field__label" />
    <SkCell fill={grow} />
  </div>
);

const SkRow2 = ({ children }) => (
  <div className="scbs-row-2col">{children}</div>
);

const SkBand = ({ color = "teal", children }) => (
  <div className={`scbs-band scbs-band--${color}`}>
    <div className="scbs-band__bar" />
    <div className="scbs-band__content">{children}</div>
  </div>
);

const SkGrowthStrategyBand = () => (
  <SkBand color="teal">
    <SkRow2>
      <SkField grow />
      <SkField grow />
    </SkRow2>
    <SkRow2>
      <SkField grow />
      <div className="scbs-drivers">
        <div className="scbs-drivers__head">
          <Sk w="55%" h={11} />
          <Sk w={80} h={11} />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="scbs-drivers__row">
            <Sk w="55%" h={11} />
            <Sk w={30} h={11} />
            <Sk w={30} h={11} />
          </div>
        ))}
        {["Revenue", "Profit"].map((n) => (
          <div key={n} className="scbs-drivers__row">
            <Sk w="40%" h={11} />
            <Sk w={50} h={11} />
            <Sk w={50} h={11} />
          </div>
        ))}
      </div>
    </SkRow2>
  </SkBand>
);

const SkGrowthPlayBand = () => (
  <SkBand color="dark">
    <Sk w="50%" h={11} className="scbs-section-header" />
    <SkRow2>
      <div className="scbs-customers-col">
        <Sk w="38%" h={11} className="scbs-field__label" />
        <SkRow2>
          <div className="scbs-customer-left">
            <SkCell />
            <div className="scbs-split-cell">
              <div className="scbs-split-cell__col">
                <Sk w="55%" h={10} />
                <Sk w="85%" h={12} className="scbs-cell__val" />
              </div>
              <div className="scbs-split-cell__divider" />
              <div className="scbs-split-cell__col">
                <Sk w="55%" h={10} />
                <Sk w="85%" h={12} className="scbs-cell__val" />
              </div>
            </div>
          </div>
          <SkCell fill />
        </SkRow2>
      </div>
      <div className="scbs-customers-col">
        <Sk w="50%" h={11} className="scbs-field__label" />
        <div className="scbs-promise-grid">
          <SkCell />
          <SkCell />
        </div>
      </div>
    </SkRow2>

    <Sk w="40%" h={11} className="scbs-section-header" />
    <SkRow2>
      <SkField grow />
      <SkField grow />
    </SkRow2>

    <SkRow2>
      {["Repeatable Model", "Economic Model"].map((label) => (
        <div key={label} className="scbs-models-col">
          <Sk w="50%" h={11} className="scbs-section-header" />
          <div className="scbs-repeatable">
            <div className="scbs-rep-row scbs-rep-row--3">
              <SkField />
              <SkField />
              <SkField />
            </div>
            <div className="scbs-rep-row scbs-rep-row--2">
              <SkField />
              <SkField />
            </div>
          </div>
        </div>
      ))}
    </SkRow2>
  </SkBand>
);

const SkCoreStrategyBand = () => (
  <SkBand color="teal">
    <SkRow2>
      <SkField grow />
      <SkField grow />
    </SkRow2>
  </SkBand>
);

const SkFooter = () => (
  <div className="scbs-footer">
    <Sk w={100} h={13} className="scbs-footer__title" />
    <div className="scbs-footer-grid">
      {[0, 1, 2, 3].map((i) => (
        <SkField key={i} />
      ))}
    </div>
  </div>
);

export const StrategyCanvasBodySkeleton = () => (
  <div className="scbs-body">
    <SkGrowthStrategyBand />
    <SkGrowthPlayBand />
    <SkCoreStrategyBand />
    <div className="scbs-body__divider" />
    <SkFooter />
  </div>
);

// ─── Root ─────────────────────────────────────────────────────────────────────

const selectCanvasModeByUniqueId = (state) => state.canvasModeByUniqueId;
const selectCanvasModeLoading = (state) => state.canvasModeLoading;

const StrategyCanvasCanvasMode = ({ briefId, companyName }) => {
  useEffect(() => {
    useStrategyCanvasReviewStore.getState().fetchCanvasMode(briefId);
  }, [briefId]);

  const canvasModeByUniqueId = useStrategyCanvasReviewStoreSelector(
    selectCanvasModeByUniqueId,
  );
  const canvasModeLoading = useStrategyCanvasReviewStoreSelector(
    selectCanvasModeLoading,
  );
  const canvasMode = canvasModeByUniqueId[briefId];

  return (
    <div className="cm-container">
      <div className="cm-banner">
        This is a read-only view of the canvas. Switch to Review Mode to review
        blocks.
      </div>

      <div className="cm-card">
        <div className="cm-card-header">
          Strategy Canvas for <strong>{companyName}</strong>
        </div>
        <div className="cm-card-divider" />

        <div className="sc-preview-panel__body">
          {canvasModeLoading || !canvasMode ? (
            <StrategyCanvasBodySkeleton />
          ) : (
            <CanvasModeBody canvasMode={canvasMode} />
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategyCanvasCanvasMode;
